import { createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Card, i18n, Icons, Typography } from '@nutrien/cxp-components';
import { useSiteFeatures } from '@nutrien/minesight-utility-module';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useCurrentBorer } from '@/rxdb/Equipment/useCurrentBorer';
import { AreaCheckResult, CuttingPermitResult, PreOpResult } from '@/rxdb/InspectionResults/types';
import { mapToChecksAdminInspectionType } from '@/utilities/inspectionHelpers';
import { jsonMapReviver } from '@/utilities/utilityFunctions';

import { useMst } from '../../mobx-models/Root';
import { InspectionResultDocument } from '../../rxdb/InspectionResults/queryBuilder';
import { InspectionResultItem } from '../../rxdb/InspectionResults/useInspectionResults';
import { InspectionDocument } from '../../rxdb/Inspections/queryBuilder';
import { InspectionOptionsWithDetails } from '../../rxdb/Inspections/useInspections';
import useLocations, { PopulatedLocation } from '../../rxdb/Locations/useLocations';
import useShifts from '../../rxdb/Shifts/useShifts';
import {
  EQUIPMENT_DEFICIENCIES_WRITE_PERMISSION,
  GROUND_HAZARD_WRITE_PERMISSION,
  InspectionType,
} from '../../utilities/constants';
import { HazardSeverity } from '../../utilities/enums';
import useEventTracking, { TrackingEventType } from '../../utilities/hooks/useEventTracking';
import useViewingCurrentShift from '../../utilities/hooks/useViewingCurrentShift';
import { sortDescendingByInspectionResultCreatedOn } from '../../utilities/sortHelper';
import { AreaCheck } from '../AddAreaCheck/AddAreaCheck';
import AddHazardsSidePanel from '../AddHazardsSidePanel';
import AreaCheckCard from '../AreaCheckCard';
import AreaCheckReadOnlyModal from '../AreaCheckReadOnlyModal';
import CuttingPermitCard from '../CuttingPermitCard';
import CuttingPermitModal from '../CuttingPermitModal';
import { blankLocation } from '../CuttingPermitModal/CuttingPermitModal';
import HazardMenu from '../HazardMenu';
import AddPreOpCheck from '../pages/Equipment/AddPreOpCheck';
import PreOpCard from '../pages/Equipment/DeficienciesChecksTab/PreOpCheckCard/PreOpCard';
import WorkFaceAreaInspectionReadOnlyModal from '../WorkFaceAreaInspectionReadOnlyModal';
import WorkFaceAreaInspectionSummary from '../WorkFaceAreaInspectionSummary';

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: '16px',
    },
    cardRoot: {
      margin: '4px !important',
      padding: '16px',
    },
    title: {
      paddingLeft: '4px',
      paddingBottom: '16px',
    },
    inspectionCards: {
      margin: '10px 0 0 -5px',
    },
  }),
);

type Props = {
  shiftCardInspectionsList: InspectionResultItem[];
};

const ChecksThisShiftCard: React.FC<Props> = ({ shiftCardInspectionsList }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user, shiftPicker } = useMst();
  const { isVanscoy } = useSiteFeatures();

  const { trackEvent } = useEventTracking();
  const viewingCurrentShift = useViewingCurrentShift();
  const { viewingMostRecentAvailableShift } = useShifts();
  const { getLocationById, populateLocation } = useLocations();
  const { isRehabBorer } = useCurrentBorer();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [inspectionsList, setInspectionsList] =
    useState<InspectionResultItem[]>(shiftCardInspectionsList);
  const [cuttingPermitOpen, setCuttingPermitOpen] = useState(false);
  const [areaCheckOpen, setAreaCheckOpen] = useState(false);
  const [selectedInspectionDetails, setSelectedInspectionDetails] = useState<InspectionDocument>();
  const [inspectionComment, setInspectionComment] = useState<string | null>('');
  const [inspectionSupervisorComment, setInspectionSupervisorComment] = useState<string | null>('');

  const [selectedInspectionOptions, setSelectedInspectionOptions] = useState<
    Record<string, InspectionOptionsWithDetails>
  >({});
  const [selectedInspectionResults, setSelectedInspectionResults] =
    useState<InspectionResultDocument>();
  const [selectedInspectionSeverity, setSelectedInspectionSeverity] = useState<HazardSeverity>();
  const [selectedAreaCheckHazards, setSelectedAreaCheckHazards] =
    useState<Record<string, string[]>>();
  const [selectedInspectionLocation, setSelectedInspectionLocation] =
    useState<PopulatedLocation | null>(blankLocation);
  const [includeLocation, setIncludeLocation] = useState(false);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null | undefined>();
  const [addHazardsOpen, setAddHazardsOpen] = useState(false);
  const [addCuttingPermitOpen, setAddCuttingPermitOpen] = useState(false);
  const [workFaceAreaInspectionOpen, setWorkFaceAreaInspectionOpen] = useState(false);
  const [selectedWorkFaceAreaInspectionId, setSelectedWorkFaceAreaInspectionId] = useState('');
  const [preOpResult, setPreOpResult] = useState<PreOpResult>();
  const [signatureId, setSignatureId] = useState<string | null>('');
  const [preOpOpen, setPreOpOpen] = useState(false);
  const [passedInspectionId, setPassedInspectionId] = useState(null);
  const hazardsMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    setInspectionsList(shiftCardInspectionsList);
  }, [shiftCardInspectionsList]);

  const checkForLocationSelected = async (inspectionResult: InspectionResultDocument) => {
    let location: PopulatedLocation | null = blankLocation;
    if (inspectionResult.locationId) {
      const locationDoc = await getLocationById(inspectionResult.locationId);
      location = await populateLocation(locationDoc);
      setIncludeLocation(true);
    } else {
      setIncludeLocation(false);
    }
    setSelectedInspectionLocation(location);
  };

  const onCuttingPermitSelected = async (inspectionResult: InspectionResultDocument) => {
    let inspectionDetail: CuttingPermitResult | string | null;

    try {
      inspectionDetail = JSON.parse(inspectionResult.inspectionDetail);
    } catch (error) {
      inspectionDetail = inspectionResult.inspectionDetail;
    }

    if (typeof inspectionDetail === 'string' || inspectionDetail === null) {
      // TODO: Handle Error
      return null;
    }

    const {
      cuttingPermitConfig,
      cuttingPermitSelections,
      cuttingPermitSeverity,
      comment,
      supervisorComment,
    } = inspectionDetail;

    // Generate selected options object for viewing cutting permit to work
    const selectedOptions = {};
    cuttingPermitConfig.categories.forEach(category => {
      const { id } = category.category;
      const selectedOptionId = cuttingPermitSelections[id].option.id;
      const option = category.options.filter(opt => opt.option.id === selectedOptionId);
      selectedOptions[id] = option[0];
    });

    await checkForLocationSelected(inspectionResult);

    setSelectedInspectionDetails(cuttingPermitConfig);
    setSelectedInspectionResults(inspectionResult);
    setSelectedInspectionOptions(selectedOptions);
    setSelectedInspectionSeverity(cuttingPermitSeverity);
    setInspectionComment(comment);
    setInspectionSupervisorComment(supervisorComment);
    setCuttingPermitOpen(true);
  };

  const onAreaCheckSelected = async (inspectionResult: InspectionResultDocument) => {
    let inspectionDetail: AreaCheckResult | string | null;

    try {
      inspectionDetail = JSON.parse(inspectionResult.inspectionDetail);
    } catch (error) {
      inspectionDetail = inspectionResult.inspectionDetail;
    }

    if (typeof inspectionDetail === 'string' || inspectionDetail === null) {
      // TODO: Handle Error
      return null;
    }

    const { areaCheckConfig, areaCheckSelections, areaCheckHazards } = inspectionDetail;

    // Generate selected options object for viewing cutting permit to work
    const selectedOptions = {};
    areaCheckHazards.forEach((x: AreaCheck) => {
      selectedOptions[x.id] = areaCheckSelections[x.id].option;
    });

    await checkForLocationSelected(inspectionResult);

    setSelectedInspectionDetails(areaCheckConfig);
    setSelectedInspectionResults(inspectionResult);
    setSelectedInspectionOptions(selectedOptions);
    setSelectedAreaCheckHazards(areaCheckHazards);
    setSelectedSignatureId(inspectionResult.signatureId);
    setAreaCheckOpen(true);
  };

  // Render inspection cards
  const getInspectionCards = () => {
    if (!inspectionsList) return [];

    return inspectionsList
      .sort(sortDescendingByInspectionResultCreatedOn)
      .map((inspection: { inspectionDetails: any; inspectionResult: any }) => {
        const { inspectionDetails, inspectionResult } = inspection;
        const inspectionType = inspectionDetails?.inspectionType;
        const checksAdminInspectionType = mapToChecksAdminInspectionType(inspectionType);
        const parsedInspectionDetail = JSON.parse(inspectionResult.inspectionDetail);

        if (isRehabBorer && isVanscoy) {
          if (parsedInspectionDetail.preOpConfig) {
            return (
              <Grid item xs={12} key={inspectionResult.id}>
                <PreOpCard
                  inspectionDetails={inspectionDetails}
                  inspectionResult={inspectionResult}
                  onSelected={viewPreOpCheck}
                />
              </Grid>
            );
          }
        }
        if (checksAdminInspectionType === InspectionType.CUTTING_PERMIT) {
          return (
            <Grid item xs={12} key={`${inspectionResult.id}-${inspectionResult.createdOn}`}>
              <CuttingPermitCard
                inspectionDetails={inspectionDetails}
                inspectionResult={inspectionResult}
                onCuttingPermitSelected={onCuttingPermitSelected}
              />
            </Grid>
          );
        }
        if (
          checksAdminInspectionType === InspectionType.WORKFACE_AREA_INSPECTION ||
          checksAdminInspectionType === InspectionType.CUSTOM_AREA_CHECK
        ) {
          if (parsedInspectionDetail?.wfaiDetails) {
            return (
              <Grid item xs={12} key={`${inspectionResult.id}-${inspectionResult.createdOn}`}>
                <WorkFaceAreaInspectionSummary
                  inspectionResult={inspectionResult}
                  onWorkFaceAreaInspectionSelected={onWorkFaceAreaInspectionSelected}
                />
              </Grid>
            );
          }

          return (
            <Grid item xs={12} key={`${inspectionResult.id}-${inspectionResult.createdOn}`}>
              <AreaCheckCard
                inspectionDetails={inspectionDetails}
                inspectionResult={inspectionResult}
                onAreaCheckSelected={onAreaCheckSelected}
              />
            </Grid>
          );
        }
        return <span key={`${uuidv4()}`} />;
      });
  };

  // area check view only
  const onOpenAreaCheckModal = () => {
    setAreaCheckOpen(true);
  };

  const onCloseAreaCheckModal = () => {
    setAreaCheckOpen(false);
  };

  const onCloseWorkFaceAreaInspectionModal = () => {
    setWorkFaceAreaInspectionOpen(false);
    setSelectedWorkFaceAreaInspectionId('');
  };

  const onWorkFaceAreaInspectionSelected = async (inspectionResult: InspectionResultDocument) => {
    setSelectedWorkFaceAreaInspectionId(inspectionResult?.id);
    setWorkFaceAreaInspectionOpen(true);

    await checkForLocationSelected(inspectionResult);
  };

  // read only cutting permit
  const onOpenCuttingPermitModal = () => {
    setCuttingPermitOpen(true);
  };

  const onCloseCuttingPermitModal = () => {
    setCuttingPermitOpen(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClosePreOp = () => {
    setPreOpOpen(false);
    handleClose();
    clearPreOpsCheckData();
  };

  const onAddAreaCheck = (inspectionId: string) => {
    history.push(`/add-areacheck?inspectionId=${inspectionId}`);
  };

  const onAddWorkAreaInspection = (inspectionId: string | undefined) => {
    history.push(`/add-wfai?inspectionId=${inspectionId}`);
  };

  const onAddCuttingPermit = (inspectionId: string) => {
    setPassedInspectionId(inspectionId);
    handleClose();
    setAddCuttingPermitOpen(true);
  };

  const onCloseAddCuttingPermitModal = () => {
    setAddCuttingPermitOpen(false);
    setAnchorEl(null);
  };

  const showAddHazardButton = useMemo(() => {
    return viewingCurrentShift || viewingMostRecentAvailableShift;
  }, [viewingCurrentShift, viewingMostRecentAvailableShift]);

  // View PreOp check
  const viewPreOpCheck = (inspectionResult: InspectionResultDocument) => {
    let inspectionDetail: PreOpResult | string | null;

    try {
      inspectionDetail = JSON.parse(inspectionResult.inspectionDetail, jsonMapReviver);
    } catch (error) {
      inspectionDetail = inspectionResult.inspectionDetail;
    }

    if (typeof inspectionDetail === 'string' || inspectionDetail === null) return;
    setSignatureId(inspectionResult.signatureId);
    setPreOpResult(inspectionDetail);
    setPreOpOpen(true);
  };

  const clearPreOpsCheckData = () => {
    setSignatureId(null);
    setPreOpResult();
  };

  return (
    <>
      <Card>
        <Grid container data-cy="ChecksThisShiftCard" className={classes.container}>
          <Grid item container xs={12}>
            <Grid item xs={10} className={classes.title}>
              <Typography variant="h3">{i18n.t('Checks this shift')}</Typography>
            </Grid>
            <Grid item container xs={2} justify="flex-end">
              {user.hasPermissionTo(GROUND_HAZARD_WRITE_PERMISSION) && showAddHazardButton && (
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    noMinHeight
                    startAdornment={<Icons.PlusFeather />}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      setAnchorEl(event.currentTarget);
                    }}
                    data-cy="add-menu"
                    id="add-menu"
                  >
                    {i18n.t('Add...')}
                  </Button>
                  <HazardMenu
                    open={hazardsMenuOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    openAddHazard={() => {
                      handleClose();
                      setAddHazardsOpen(true);
                      trackEvent(TrackingEventType.HAZARD_ADD_OPEN, {
                        location: 'CHECKS_THIS_SHIFT',
                      });
                    }}
                    onAddAreaCheck={onAddAreaCheck}
                    onAddWorkAreaInspection={onAddWorkAreaInspection}
                    onAddCuttingPermit={onAddCuttingPermit}
                    isCurrentShiftSelected={shiftPicker.isCurrentShiftSelected()}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} container className={classes.inspectionCards}>
            {getInspectionCards()}
          </Grid>
        </Grid>
      </Card>
      {areaCheckOpen && (
        <AreaCheckReadOnlyModal
          open={areaCheckOpen}
          onClose={onCloseAreaCheckModal}
          onOpen={onOpenAreaCheckModal}
          inspectionDetailsSource={selectedInspectionDetails}
          selectedOptionsSource={selectedInspectionOptions}
          areaChecks={selectedAreaCheckHazards}
          signatureId={selectedSignatureId}
          createdOn={selectedInspectionResults?.createdOn}
          inspectionLocation={includeLocation ? selectedInspectionLocation : null}
        />
      )}
      {workFaceAreaInspectionOpen && (
        <WorkFaceAreaInspectionReadOnlyModal
          open={workFaceAreaInspectionOpen}
          onClose={onCloseWorkFaceAreaInspectionModal}
          inspectionId={selectedWorkFaceAreaInspectionId}
          inspectionLocation={includeLocation ? selectedInspectionLocation : null}
        />
      )}
      <AddHazardsSidePanel
        open={addHazardsOpen}
        onClose={() => {
          setAddHazardsOpen(false);
        }}
        onOpen={() => {
          setAddHazardsOpen(true);
        }}
      />
      {/* TODO: Refactor so it's not necessary to render it twice */}
      {/* Create Cutting Permit Modal */}
      <CuttingPermitModal
        open={addCuttingPermitOpen}
        onClose={onCloseAddCuttingPermitModal}
        onOpen={onAddCuttingPermit}
        inspectionDetailsSource={selectedInspectionDetails}
        selectedOptionsSource={selectedInspectionOptions}
        severityLevel={selectedInspectionSeverity}
        createdOn={selectedInspectionResults?.createdOn}
        inspectionId={passedInspectionId}
      />
      {/* Modify Cutting Permit Modal */}
      <CuttingPermitModal
        open={cuttingPermitOpen}
        onClose={onCloseCuttingPermitModal}
        onOpen={onOpenCuttingPermitModal}
        inspectionDetailsSource={selectedInspectionDetails}
        selectedOptionsSource={selectedInspectionOptions}
        severityLevel={selectedInspectionSeverity}
        createdOn={selectedInspectionResults?.createdOn}
        signatureId={selectedInspectionResults?.signatureId}
        inspectionComment={inspectionComment}
        inspectionLocation={includeLocation ? selectedInspectionLocation : null}
        inspectionSupervisorComment={inspectionSupervisorComment}
        inspectionResultId={selectedInspectionResults?.id}
        inspectionId={passedInspectionId}
      />
      <AddPreOpCheck
        open={preOpOpen}
        onClose={onClosePreOp}
        onOpen={onAddAreaCheck}
        viewOnly={
          preOpResult !== undefined ||
          !user.hasPermissionTo(EQUIPMENT_DEFICIENCIES_WRITE_PERMISSION)
        }
        inspectionDetailsSource={preOpResult?.preOpConfig}
        selectedOptionsSource={preOpResult?.preOpSelections}
        createdEquipmentDeficiencies={preOpResult?.preOpDeficiencies}
        signatureId={signatureId}
        inspectionId={passedInspectionId}
      />
    </>
  );
};

export default observer(ChecksThisShiftCard);
